// Tag attributes are dynamic — one key per organisation tag — so they are identified by prefix.
export const TAG_PREFIX = 'tag_';
export const DEMAND_TAG_PREFIX = 'demand_tag_';
export const SUPPLY_TAG_PREFIX = 'supply_tag_';
export const DEAL_TAG_PREFIX = 'deal_tag_';
/** The demand metrics column of a supply tag of the allocated supply, for example `allocated_supply_tag_<name>`. */
export const ALLOCATED_SUPPLY_TAG_PREFIX = `allocated_${SUPPLY_TAG_PREFIX}`;

/**
 * Matching analysis keys a tag filter by its side, `supply_tag_<name>` or `demand_tag_<name>`, which are also the
 * names the portfolio metrics take.
 */
export function isSideTagKey(key: string): boolean {
  return key.startsWith(SUPPLY_TAG_PREFIX) || key.startsWith(DEMAND_TAG_PREFIX);
}

/** The colId and field of the pivot table column of a tag, for example `supply_tag_<name>`. */
export function tagColumnId(prefix: string, tagName: string): string {
  return `${prefix}${tagName}`;
}

export function isAnyTagKey(key: string): boolean {
  return key.startsWith(TAG_PREFIX) || isSideTagKey(key);
}

/** Any key that is not a tag filter key is returned as it is. */
export function tagNameFromKey(key: string): string {
  const prefix = [SUPPLY_TAG_PREFIX, DEMAND_TAG_PREFIX, TAG_PREFIX].find((tagPrefix) => key.startsWith(tagPrefix));

  return prefix ? key.slice(prefix.length) : key;
}

/**
 * The API namespaces the dynamic demand tag dimensions as `demand_tag_<name>`, while a dynamic filter can key
 * them by `tag_<name>`. Only a `tag_<name>` key is renamed; every other key passes through unchanged.
 */
export function toDemandTagKey(filterKey: string): string {
  return filterKey.startsWith(TAG_PREFIX) ? `${DEMAND_TAG_PREFIX}${filterKey.slice(TAG_PREFIX.length)}` : filterKey;
}

/** The supply counterpart of {@link toDemandTagKey}, with the same contract. */
export function toSupplyTagKey(filterKey: string): string {
  return filterKey.startsWith(TAG_PREFIX) ? `${SUPPLY_TAG_PREFIX}${filterKey.slice(TAG_PREFIX.length)}` : filterKey;
}

export type HashValue = Record<string, Array<string | null>>;

export type TagValues = {
  tag: string;
  values: Array<string | null>;
};

export const transformHashValues = (
  hashValues: Array<HashValue>,
  omit: Array<string> = [],
  nullify = true,
): TagValues[] => {
  return hashValues
    .map((entry) => {
      const [key] = Object.keys(entry);
      const values = entry[key].map((value) => (nullify && value === 'null' ? null : value));
      return { tag: key, values };
    })
    .filter((entry) => !omit.includes(entry.tag));
};

const EXCLUDED_VALUES = ['consumers', 'start_datetime', 'end_datetime', 'matching_res'];
export function hashValuesToRecord(hashValues?: Array<HashValue>): HashValue | undefined {
  if (!hashValues || !hashValues.length) {
    return undefined;
  }

  return hashValues.reduce<HashValue | undefined>((acc, item) => {
    const filteredItems = Object.keys(item).filter((key) => !EXCLUDED_VALUES.includes(key));

    if (filteredItems.length) {
      const result: HashValue = acc ?? {};

      for (const key of filteredItems) {
        if (!result[key]) {
          result[key] = [];
        }
        result[key] = result[key].concat(item[key].map((value) => (value === 'null' ? null : value)));
      }

      return result;
    }

    return acc;
  }, undefined);
}
